/**
 * 送れなかったときの画面の中身（README ㉛d）と、あとから断られたときの通知の文。
 * 画面に触れない純粋な関数なので単体テストできる。
 */
import type { GameState } from '../../api/types';
import type { RpcError } from '../../api/errors';

export type FailVariant = 'net' | 'dup' | 'hornet' | 'photo' | 'other';
export type FailAction = 'flush' | 'back' | 'resend' | 'reshoot';

export interface FailButton {
  action: FailAction;
  label: string;
  /** true＝墨の地のボタン（主）。false＝枠だけ */
  primary: boolean;
}

export interface FailScreen {
  variant: FailVariant;
  /** 見出しの上の1行（空なら出さない） */
  status: string;
  heading: string;
  /** 補足（空なら出さない。補足の文には句点を付ける） */
  text: string;
  buttons: FailButton[];
}

export interface FailInput {
  kind: 'spot' | 'prompt';
  /** 送信待ちの件数（圏外のとき） */
  pending?: number;
  /** 襲来の送り主チーム名と残り（ミリ秒） */
  hornet?: { sourceName: string | null; remainingMs: number } | null;
}

export function backLabel(kind: 'spot' | 'prompt'): string {
  return kind === 'spot' ? '図鑑に戻る' : 'お題選択に戻る';
}

/** 画面の分岐。code は RpcError.code、queued は圏外で送信待ちになったとき */
export function failVariantOf(code: string | 'queued'): FailVariant {
  if (code === 'queued' || code === 'network') return 'net';
  if (code === 'already_submitted') return 'dup';
  if (code === 'area_blocked') return 'hornet';
  if (code === 'photo_unreadable') return 'photo';
  return 'other';
}

export function fmtMinutesLeft(ms: number): string {
  return `${Math.max(1, Math.ceil(ms / 60000))}分`;
}

export function failScreen(code: string | 'queued', input: FailInput): FailScreen {
  const variant = failVariantOf(code);
  const back = backLabel(input.kind);
  switch (variant) {
    case 'net':
      return {
        variant,
        status: `送信待ち ${input.pending ?? 1}件`,
        heading: '電波が届いていません',
        text: input.kind === 'spot' ? '電波が戻ると自動で送り、ダイスを振ります。' : '電波が戻ると自動で送ります。',
        buttons: [
          { action: 'flush', label: '今すぐ送り直す', primary: true },
          { action: 'back', label: back, primary: false },
        ],
      };
    case 'dup':
      return {
        variant,
        status: '',
        heading: input.kind === 'spot' ? 'このスポットはクリア済みです' : 'このお題は手に入れ済みです',
        text: 'チームのほかの人が先に提出しました。',
        buttons: [{ action: 'back', label: back, primary: true }],
      };
    case 'hornet': {
      const h = input.hornet;
      const who = h?.sourceName ? `スズメバチの襲来（${h.sourceName}）` : 'スズメバチの襲来';
      return {
        variant,
        status: h ? `${who}　残り ${fmtMinutesLeft(h.remainingMs)}` : who,
        heading: '襲来中のため提出できません',
        text: '',
        // 写真は残さない（襲来中に撮った写真があとで通らないように）
        buttons: [{ action: 'back', label: back, primary: true }],
      };
    }
    case 'photo':
      return {
        variant,
        status: '',
        heading: '写真を読み込めませんでした',
        text: 'もう一度撮ってください。',
        buttons: [
          { action: 'reshoot', label: '撮り直す', primary: true },
          { action: 'back', label: back, primary: false },
        ],
      };
    default: {
      // 理由が分かるものは書く（本人11/4「送れませんでした」だけだと原因が分からない）。
      // 分からないものはコードを小さく出し、運営に伝えられるようにする
      const r = OTHER_REASONS[code];
      return {
        variant: 'other',
        status: r ? '' : `エラー：${code}`,
        heading: r?.heading ?? '送れませんでした',
        text: r?.text ?? '',
        buttons: r && !r.retry
          ? [{ action: 'back', label: back, primary: true }]
          : [
              { action: 'resend', label: 'もう一度送る', primary: true },
              { action: 'back', label: back, primary: false },
            ],
      };
    }
  }
}

/** その他の断りの理由。heading は荷札にも使うので1行・句点なし。retry＝送り直して通る見込みがある */
const OTHER_REASONS: Record<string, { heading: string; text: string; retry: boolean }> = {
  event_not_running: { heading: '開催中ではないため提出できません', text: '開催前か一時停止中です。始まってから提出してください。', retry: false },
  event_ended: { heading: 'ゲームは終了しました', text: '', retry: false },
  outside_area: { heading: 'お題のエリアの外にいます', text: '位置がエリアの外と判定されました。エリアの中に入ってから送り直してください。', retry: true },
  prompts_locked: { heading: 'お題はまだ解禁されていません', text: 'スポットをもう少しクリアすると、お題を出せます。', retry: false },
  prompt_blocked: { heading: 'このお題は撮れません', text: '交換所でもらえます。', retry: false },
  already_have: { heading: 'このお題はもう持っています', text: '', retry: false },
  invalid_session: { heading: 'ログインし直してください', text: 'アプリを開き直すとログインの画面に戻ります。', retry: false },
  terms_not_accepted: { heading: '規約への同意が済んでいません', text: 'アプリを開き直して同意してください。', retry: false },
  target_not_found: { heading: '提出先が見つかりません', text: 'アプリを開き直してください。', retry: false },
};

/** あとから（圏外から戻って）断られたときの荷札の本文。1行・句点なし */
export function settledNoticeBody(code: string, kind: 'spot' | 'prompt'): string {
  switch (failVariantOf(code)) {
    case 'dup':
      return kind === 'spot' ? 'このスポットはクリア済みです' : 'このお題は手に入れ済みです';
    case 'hornet':
      return '襲来中のため提出できません';
    default:
      return OTHER_REASONS[code]?.heading ?? `送れませんでした（${code}）`;
  }
}

export const SETTLED_NOTICE_HEADING = '提出';

/** 自チームがいま出せなくなっているエリアの襲来（送り主と残り）。無ければ null */
export function hornetInfo(state: GameState | null, areaId: string, nowMs: number): { sourceName: string | null; remainingMs: number } | null {
  if (!state) return null;
  const area = state.areas.find((a) => a.area_id === areaId);
  const until = area?.blocked_until ? Date.parse(area.blocked_until) : 0;
  if (!until || until <= nowMs) return null;
  const me = state.team.team_id;
  const src = state.active_effects.find((e) => e.effect_type === 'block_area' && e.area_id === areaId && e.target_team === me);
  return { sourceName: src?.source_team_name ?? null, remainingMs: until - nowMs };
}

/** エラーコードの取り出し（RpcError 以外が来ても落とさない） */
export function codeOf(e: RpcError | { code?: string } | null | undefined): string {
  return e?.code ?? 'unknown';
}
